import { EditorState, SelectionState } from 'draft-js';
import { motion } from 'framer-motion';
import React, { SetStateAction, useState, useEffect } from 'react';
import { useRef } from 'react';
import CustomDialog from '../../shared/CustomDialog';
import AddToDictionaryModal from '../DictionaryModal/AddToDictionaryModal';
import { IDictionaryModalProps } from '../DictionaryModal/IDictionaryModal';
import EntriesModalContent from './EntriesModalContent';
import { IModal, IReplaceWordsModalProps, ISpeaker, ModalVariantsEnums } from './IEditor';
import ReplaceWordsModalContent from './ReplaceWordsModalContent';
import SpeakersModalContent from './SpeakersModalContent';
import { ResizableBox } from 'react-resizable';
import SummaryModalContent from './SummaryModalContent';
import 'react-resizable/css/styles.css';

interface IProps {
  modal: IModal;
  setModal: React.Dispatch<SetStateAction<IModal>>;
  handleLeaveEditor: (variant: ModalVariantsEnums) => void;
  setEditorState: React.Dispatch<SetStateAction<EditorState>>;
  closeDictionaryModal: () => void;
  setSpeakerToSelection: (sel: SelectionState, speaker?: ISpeaker) => void;
  replaceSpeaker: (newSpeaker: ISpeaker, oldSpeaker: ISpeaker, replaceAll: boolean, blockKey: string) => void;
  dictionaryModalProps: IDictionaryModalProps;
  replaceWordsModalProps: IReplaceWordsModalProps;
  editorState: EditorState;
  currentSessionId: number | null;
}

const modalVariants = {
  visible: {
    transform: 'translate3d(0,0,100px)',
    zIndex: 100,
    opacity: 1,
    transition: { ease: 'easeInOut', duration: 0.2 },
  },
  hidden: {
    transform: 'translate3d(0,0,-1px)',
    zIndex: 100,
    opacity: 0,
    transition: { ease: 'easeInOut', duration: 0.2 },
  },
  containerHidden: {
    transform: 'scale(0.9)',
    transition: { ease: 'easeInOut', duration: 0.2 },
  },
  containerVisible: {
    transform: 'scale(1)',
    transition: { ease: 'easeInOut', duration: 0.2 },
  },
};

const ModalsWrapper = ({
  modal,
  setModal,
  setEditorState,
  handleLeaveEditor,
  closeDictionaryModal,
  replaceSpeaker,
  setSpeakerToSelection,
  replaceWordsModalProps,
  dictionaryModalProps,
  editorState,
  currentSessionId,
}: IProps) => {
  const { variant, show } = modal;
  const modalRef = useRef<HTMLDivElement>(null);

  // State for Summary modal resizing
  const [summarySize, setSummarySize] = useState({ width: 0, height: 0 });
  const [maxConstraints, setMaxConstraints] = useState<[number, number]>([1600, 1600]);

  // Calculate max constraints based on viewport size
  useEffect(() => {
    const calculateMaxConstraints = () => {
      // Account for padding and margins (20px on each side = 40px total)
      const padding = 40;
      const maxWidth = window.innerWidth - padding;
      const maxHeight = window.innerHeight - padding;
      setMaxConstraints([maxWidth, maxHeight]);

      // Adjust current size if it exceeds new constraints
      setSummarySize((prev) => ({
        width: Math.min(prev.width || maxWidth, maxWidth),
        height: Math.min(prev.height || maxHeight, maxHeight),
      }));
    };

    calculateMaxConstraints();
    window.addEventListener('resize', calculateMaxConstraints);

    return () => {
      window.removeEventListener('resize', calculateMaxConstraints);
    };
  }, []);

  // Reset size when modal opens
  useEffect(() => {
    if (variant === ModalVariantsEnums.SUMMARY && show) {
      const padding = 40;
      const maxWidth = window.innerWidth - padding;
      const maxHeight = window.innerHeight - padding;
      const minWidth = Math.min(800, maxWidth);
      const minHeight = Math.min(600, maxHeight);
      const initialWidth = Math.min(Math.max(window.innerWidth * 0.7, minWidth), maxWidth);
      const initialHeight = Math.min(Math.max(window.innerHeight * 0.85, minHeight), maxHeight);
      setSummarySize({ width: initialWidth, height: initialHeight });
      setMaxConstraints([maxWidth, maxHeight]);
    }
  }, [variant, show]);

  return (
    <motion.div
      className="modal_overlay"
      variants={modalVariants}
      initial="hidden"
      animate="visible"
      exit="hidden"
      onClick={(e: any) => {
        // Check if the click is on a MUI dropdown element
        // Ensure e.target is a DOM element before calling closest
        const target = e.target as Element;
        const isMuiDropdown =
          target &&
          target.closest &&
          (target.closest('.MuiSelect-root') ||
            target.closest('.MuiMenu-root') ||
            target.closest('.MuiPopover-root') ||
            target.closest('[role="listbox"]') ||
            target.closest('[role="option"]'));

        // Ensure target is a Node before calling contains
        const isTargetNode = target && target.nodeType === Node.ELEMENT_NODE;

        if (
          modalRef &&
          modalRef.current &&
          isTargetNode &&
          !modalRef.current.contains(target as Node) &&
          modal.variant !== ModalVariantsEnums.ENTRIES &&
          !isMuiDropdown
        ) {
          setModal({ ...modal, show: false });
        }
      }}
    >
      <motion.div
        className="modal_container"
        variants={modalVariants}
        initial="containerHidden"
        animate="containerVisible"
        exit="containerHidden"
        ref={modalRef}
      >
        {variant === ModalVariantsEnums.REPLACE_WORDS && (
          <CustomDialog>
            <ReplaceWordsModalContent
              replaceWordsModalProps={replaceWordsModalProps}
              editorState={editorState}
              setEditorState={setEditorState}
              setModal={setModal}
            />
          </CustomDialog>
        )}

        {variant === ModalVariantsEnums.SUMMARY && currentSessionId !== null && (
          <ResizableBox
            width={summarySize.width || Math.min(window.innerWidth * 0.7, 1200)}
            height={summarySize.height || Math.min(window.innerHeight * 0.85, 900)}
            minConstraints={[Math.min(800, window.innerWidth - 40), Math.min(600, window.innerHeight - 40)]}
            maxConstraints={maxConstraints}
            onResize={(e, data) => {
              // Constrain the size to ensure it never goes off screen
              const padding = 40;
              const maxWidth = window.innerWidth - padding;
              const maxHeight = window.innerHeight - padding;
              const minWidth = Math.min(800, maxWidth);
              const minHeight = Math.min(600, maxHeight);

              const constrainedWidth = Math.min(Math.max(data.size.width, minWidth), maxWidth);
              const constrainedHeight = Math.min(Math.max(data.size.height, minHeight), maxHeight);

              setSummarySize({ width: constrainedWidth, height: constrainedHeight });
            }}
            style={{ backgroundColor: '#f2f2f2', padding: '30px', display: 'flex', flexDirection: 'column' }}
          >
            <div
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }}
              className="summary_modal_resizable_wrapper"
            >
              <SummaryModalContent
                onClose={() => setModal({ ...modal, show: false })}
                editorState={editorState}
                sessionId={currentSessionId.toString()}
              />
            </div>
          </ResizableBox>
        )}

        {variant === ModalVariantsEnums.SPEAKERS && (
          <CustomDialog>
            <SpeakersModalContent
              editorState={editorState}
              setEditorState={setEditorState}
              setModal={setModal}
              replaceSpeaker={replaceSpeaker}
              setSpeakerToSelection={setSpeakerToSelection}
            />
          </CustomDialog>
        )}

        {variant === ModalVariantsEnums.ENTRIES && (
          // <CustomDialog>
          <ResizableBox
            style={{ backgroundColor: '#f2f2f2', padding: '30px' }}
            width={560}
            height={660}
            minConstraints={[560, 660]}
            maxConstraints={[1600, 1600]}
          >
            <EntriesModalContent setModal={setModal} />
          </ResizableBox>
          // </CustomDialog>
        )}

        {variant === ModalVariantsEnums.DISCARD && (
          <CustomDialog
            message={
              // localStorage.getItem('EditorState')
              //   ? 'Zadnje verzije besedila niste shranili. Ali želite vseeno zapustiti urejevalnik?'
              variant === ModalVariantsEnums.DISCARD
                ? 'Ali ste prepričani, da želite zavreči zadnje spremembe in zapustiti urejevalnik?'
                : 'Ali ste prepričani, da zapustiti urejevalnik?'
            }
            onClickNo={() => setModal({ ...modal, show: false })}
            onClickYes={() => handleLeaveEditor(variant)}
            noButtonName="Prekliči"
            yesButtonName={'Zavrzi'}
          />
        )}

        {variant === ModalVariantsEnums.BLOCKS && (
          <CustomDialog
            message="Zaradi dolžine besedila je priporočljivo, da se ga razbije na bloke."
            onClickNo={() => {
              setModal({ ...modal, show: false });
            }}
            onClickYes={() => {
              setModal({ ...modal, show: false });
            }}
            noButtonName="Prekliči"
            yesButtonName="Strinjam se"
          />
        )}

        {variant === ModalVariantsEnums.ADD_TO_DICT && (
          <AddToDictionaryModal
            onClose={closeDictionaryModal}
            opened={dictionaryModalProps.open}
            word={dictionaryModalProps.word}
          />
        )}
      </motion.div>
    </motion.div>
  );
};

export default ModalsWrapper;
